import { useAuth } from '@/contexts/AuthContext'
import { getUserRoleFlags } from '@/components/layout/navigationSections'
import { useAccessibleAffiliates, type AccessibleAffiliate } from './useAccessibleAffiliates'

export interface AffiliateLeftPlatformGate {
  /**
   * True when the current user's ONLY affiliate access is affiliates that
   * have left the platform: the account is view-only (every edit control is
   * disabled) and Discovery is hidden.
   */
  readOnly: boolean
  /** Ids of the user's accessible affiliates that have left. */
  leftAffiliateIds: string[]
  /** False while the flag is still being fetched for gated users. */
  isResolved: boolean
}

/** #1324 — case-insensitive "Left" test shared by the gate and the page badge. */
export function hasAffiliateLeft(a: Pick<AccessibleAffiliate, 'status' | 'hasLeftPlatform'> | null | undefined): boolean {
  if (!a) return false
  if (a.hasLeftPlatform === true) return true
  return (a.status ?? '').trim().toLowerCase() === 'left'
}

/**
 * #1324 — "Affiliate has left the platform" view-only gate.
 *
 * Mirrors useDedicatedOnlyAffiliate: only affiliate-only non-admins ever
 * fire the /affiliates/mine request (shared 5-minute cache), everyone else
 * resolves synchronously, and an empty affiliate list resolves to
 * readOnly=false — absence of data must never lock the UI.
 *
 * The rule is "ALL accessible affiliates have left": a user with one Left
 * and one live affiliate legitimately keeps editing the live one. This hook
 * is UX only; the server rejects writes (403 AFFILIATE_LEFT_PLATFORM) and
 * Discovery reads regardless of what the UI shows.
 */
export function useAffiliateLeftPlatform(): AffiliateLeftPlatformGate {
  const { user } = useAuth()
  const { isAdmin, hasHotelAccess, hasAffiliateAccess } = getUserRoleFlags(user)
  const needsCheck = !!user && !isAdmin && !hasHotelAccess && hasAffiliateAccess

  const { affiliates, isLoading } = useAccessibleAffiliates({ enabled: needsCheck })

  if (!needsCheck) {
    return { readOnly: false, leftAffiliateIds: [], isResolved: true }
  }
  const leftAffiliateIds = affiliates.filter(hasAffiliateLeft).map(a => a.id)
  return {
    readOnly: affiliates.length > 0 && leftAffiliateIds.length === affiliates.length,
    leftAffiliateIds,
    isResolved: !isLoading,
  }
}
